import { useEffect, RefObject, useRef } from "react";
import type { Virtualizer } from "@tanstack/react-virtual";
import { findScrollParent, scrollIntoViewInContainer } from "../utils/scrollUtils";

interface UseScrollToActiveRowProps {
	targetIndex: number | null;
	shouldVirtualize: boolean;
	rowVirtualizer: Virtualizer<HTMLElement, Element>;
	activeRowRefDiv: RefObject<HTMLDivElement>;
	activeRowRefTr: RefObject<HTMLTableRowElement>;
	displayedValuesLength: number;
}

/**
 * Hook for handling scroll-to-active-row behavior
 * Scrolls the active row into view when it becomes active
 */
export function useScrollToActiveRow({
	targetIndex,
	shouldVirtualize,
	rowVirtualizer,
	activeRowRefDiv,
	activeRowRefTr,
	displayedValuesLength,
}: UseScrollToActiveRowProps) {
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		// Clear any pending timeout
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}

		if (targetIndex == null || targetIndex < 0 || targetIndex >= displayedValuesLength) return;

		const scrollToActiveRow = () => {
			if (shouldVirtualize) {
				const scrollElement = rowVirtualizer.options.getScrollElement?.();

				// Try virtualizer's scrollToIndex first (brings row into view)
				try {
					rowVirtualizer.scrollToIndex(targetIndex, {
						align: "center",
						behavior: "smooth",
					});
				} catch (e) {
					// Virtualizer failed, will use manual fallback
				}

				const scrollTo = () => {
					if (activeRowRefDiv.current && scrollElement) {
						scrollIntoViewInContainer(activeRowRefDiv.current, scrollElement, {
							behavior: "smooth",
							block: "center",
						});
						return;
					}
				};

				timeoutRef.current = setTimeout(() => {
					scrollTo();
				}, 100);
			}
		};

		requestAnimationFrame(scrollToActiveRow);

		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
				timeoutRef.current = null;
			}
		};
	}, [targetIndex, shouldVirtualize, rowVirtualizer, displayedValuesLength, activeRowRefDiv, activeRowRefTr]);
}
